import React from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "@material-ui/core/Card";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "../../../Utils/FormInputs/TextField";
import Label from "../../../Utils/FormInputs/Label";
import { RightAlignedContainer } from "../../../Utils/styledComponents";
import { CustomSelect } from "../../../Utils/FormInputs/SelectInput";
import PreviousPregnancyTable from "./PreviousPregnancyTable";

const STEPS = [
    "Clinical Information",
    "Medical & Surgical History",
    "Previous Pregnancies",
    "Demographics & Next of Kin",
];

const useStyles = makeStyles((theme) => ({
    stepper: {
        padding: "12px 0",
        backgroundColor: "transparent",
        "& .MuiStepLabel-label": {
            fontSize: "12px",
        },
        "& .MuiStepIcon-root.MuiStepIcon-active": {
            color: "#1976d2",
        },
        "& .MuiStepIcon-root.MuiStepIcon-completed": {
            color: "#4caf50",
        },
    },
    gestationalBadge: {
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "16px",
        fontSize: "13px",
        fontWeight: 600,
        marginLeft: "8px",
    },
    trimester1: {
        backgroundColor: "#e3f2fd",
        color: "#1565c0",
    },
    trimester2: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
    },
    trimester3: {
        backgroundColor: "#fff3e0",
        color: "#e65100",
    },
    infoCard: {
        padding: "12px 16px",
        marginBottom: "16px",
        borderLeft: "4px solid #1976d2",
        backgroundColor: "#f5f9ff",
        borderRadius: "4px",
    },
    sectionTitle: {
        fontSize: "14px",
        fontWeight: 600,
        marginBottom: "12px",
        color: "#333",
        borderBottom: "1px solid #e0e0e0",
        paddingBottom: "6px",
    },
    checkboxGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: "0px",
    },
    checkboxLabel: {
        "& .MuiTypography-body1": {
            fontSize: "13px",
        },
        "& .MuiCheckbox-root": {
            padding: "4px 8px",
        },
    },
}));

/**
 * ANCBookingForm — Multi-step form for ANC Registration & Booking
 * Follows Uganda MOH HMIS 071 guidelines and WHO 8-contact ANC model
 */
const ANCBookingForm = ({
    // Form data
    bookingData,
    medicalHistory,
    surgicalHistory,
    previousPregnancies,
    // Computed values
    gestationalAge,
    trimester,
    // UI state
    submitted,
    loading,
    activeStep,
    existingBooking,
    // Handlers
    handleChange,
    handleSelectChange,
    handleMedicalHistoryChange,
    handleSurgicalHistoryChange,
    handleAddPregnancy,
    handleRemovePregnancy,
    handlePregnancyChange,
    handleNext,
    handleBack,
    handleStepClick,
    handleSubmit,
    // Validation
    validateStep,
    isFormValid,
    // Constants
    BLOOD_GROUP_OPTIONS,
    MARITAL_STATUS_OPTIONS,
    EDUCATION_LEVEL_OPTIONS,
    RELATIONSHIP_OPTIONS,
    MEDICAL_HISTORY_CHECKLIST,
    SURGICAL_HISTORY_CHECKLIST,
    DELIVERY_MODE_OPTIONS,
    DELIVERY_PLACE_OPTIONS,
    OUTCOME_OPTIONS,
    SEX_OPTIONS,
}) => {
    const classes = useStyles();
    const isSubmitting = loading === "submitting";
    const isEditing = !!existingBooking;
    const gravida = parseInt(bookingData.gravida) || 0;

    // ─── Gestational Age Summary Bar ─────────────────────────────────
    const gestationalSummary = bookingData.lnmp ? (
        <div className={classes.infoCard}>
            <Row>
                <Col sm={3}>
                    <strong style={{ fontSize: "12px" }}>LNMP:</strong>{" "}
                    <span style={{ fontSize: "13px" }}>{bookingData.lnmp}</span>
                </Col>
                <Col sm={3}>
                    <strong style={{ fontSize: "12px" }}>EDD:</strong>{" "}
                    <span style={{ fontSize: "13px" }}>{bookingData.edd}</span>
                </Col>
                <Col sm={3}>
                    <strong style={{ fontSize: "12px" }}>Gestational Age:</strong>{" "}
                    <span style={{ fontSize: "13px" }}>
                        {gestationalAge.display || "—"}
                    </span>
                </Col>
                <Col sm={3}>
                    {trimester && (
                        <span
                            className={`${classes.gestationalBadge} ${
                                trimester.includes("1st")
                                    ? classes.trimester1
                                    : trimester.includes("2nd")
                                    ? classes.trimester2
                                    : classes.trimester3
                            }`}
                        >
                            {trimester}
                        </span>
                    )}
                </Col>
            </Row>
        </div>
    ) : null;

    // ─── Step 0: Clinical Information ────────────────────────────────
    const clinicalInfoStep = (
        <div>
            <h6 className={classes.sectionTitle}>Obstetric & Clinical Information</h6>
            <Row>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Last Normal Menstrual Period (LNMP)" type />
                    <TextField
                        type="date"
                        name="lnmp"
                        value={bookingData.lnmp}
                        onChange={handleChange}
                        submitted={submitted}
                        max={new Date().toISOString().split("T")[0]}
                    />
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Expected Date of Delivery (EDD)" />
                    <TextField
                        type="date"
                        name="edd"
                        value={bookingData.edd}
                        onChange={handleChange}
                        disabled
                    />
                    <small className="text-muted">Auto-calculated from LNMP</small>
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Gestational Age at Booking" />
                    <div
                        style={{
                            padding: "6px 12px",
                            border: "1px solid #ced4da",
                            borderRadius: "10px",
                            backgroundColor: "#f8f9fa",
                            fontSize: "14px",
                            minHeight: "34px",
                        }}
                    >
                        {gestationalAge.display || "Enter LNMP to calculate"}
                    </div>
                </Col>
            </Row>
            <Row>
                <Col sm={6} md={3} className="mb-3">
                    <Label name="Gravida" type />
                    <TextField
                        type="number"
                        name="gravida"
                        value={bookingData.gravida}
                        onChange={handleChange}
                        submitted={submitted}
                        min="1"
                        max="20"
                        placeholder="Total pregnancies"
                    />
                    <small className="text-muted">
                        Including current pregnancy
                    </small>
                </Col>
                <Col sm={6} md={3} className="mb-3">
                    <Label name="Parity" type />
                    <TextField
                        type="number"
                        name="parity"
                        value={bookingData.parity}
                        onChange={handleChange}
                        submitted={submitted}
                        min="0"
                        max="20"
                        placeholder="Deliveries ≥ 28 weeks"
                    />
                </Col>
                <Col sm={6} md={3} className="mb-3">
                    <Label name="Height (cm)" />
                    <TextField
                        type="number"
                        name="height_cm"
                        value={bookingData.height_cm}
                        onChange={handleChange}
                        min="100"
                        max="250"
                        placeholder="cm"
                    />
                </Col>
                <Col sm={6} md={3} className="mb-3">
                    <Label name="Pre-Pregnancy Weight (kg)" />
                    <TextField
                        type="number"
                        name="pre_pregnancy_weight_kg"
                        value={bookingData.pre_pregnancy_weight_kg}
                        onChange={handleChange}
                        min="20"
                        max="250"
                        placeholder="kg"
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Blood Group" />
                    <CustomSelect
                        name="blood_group"
                        value={BLOOD_GROUP_OPTIONS.find(
                            (o) => o.value === bookingData.blood_group
                        )}
                        onChange={(val) => handleSelectChange("blood_group", val)}
                        options={BLOOD_GROUP_OPTIONS}
                        defaultValue={{ label: "Select blood group...", value: "" }}
                    />
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Rhesus Factor" />
                    <Form.Control
                        as="select"
                        name="rhesus_factor"
                        value={bookingData.rhesus_factor}
                        onChange={handleChange}
                        className="form__control"
                    >
                        <option value="">Select...</option>
                        <option value="positive">Positive (+)</option>
                        <option value="negative">Negative (−)</option>
                    </Form.Control>
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Allergies" />
                    <TextField
                        type="text"
                        name="allergies"
                        value={bookingData.allergies}
                        onChange={handleChange}
                        placeholder="e.g. Penicillin, Sulpha drugs"
                    />
                </Col>
            </Row>
            {/* Risk alert for high-risk indicators */}
            {(gravida >= 5 ||
                parseInt(bookingData.parity) >= 5 ||
                gestationalAge.weeks > 28 ||
                bookingData.rhesus_factor === "negative") && (
                <Alert severity="warning" style={{ fontSize: "12px", marginTop: "8px" }}>
                    <strong>High-Risk Indicators Detected:</strong>
                    <ul style={{ margin: "4px 0 0 0", paddingLeft: "16px" }}>
                        {gravida >= 5 && <li>Grand multigravida (Gravida ≥ 5)</li>}
                        {parseInt(bookingData.parity) >= 5 && (
                            <li>Grand multiparity (Parity ≥ 5)</li>
                        )}
                        {gestationalAge.weeks > 28 && (
                            <li>Late ANC booking (&gt; 28 weeks)</li>
                        )}
                        {bookingData.rhesus_factor === "negative" && (
                            <li>Rhesus negative — requires Anti-D prophylaxis</li>
                        )}
                    </ul>
                </Alert>
            )}
        </div>
    );

    // ─── Step 1: Medical & Surgical History ──────────────────────────
    const medicalHistoryStep = (
        <div>
            <h6 className={classes.sectionTitle}>Medical History</h6>
            <p className="text-muted" style={{ fontSize: "12px", marginBottom: "8px" }}>
                Check all conditions that the mother has been diagnosed with or has a
                history of.
            </p>
            <div className={classes.checkboxGrid}>
                {MEDICAL_HISTORY_CHECKLIST.map((item) => (
                    <FormControlLabel
                        key={item.key}
                        className={classes.checkboxLabel}
                        control={
                            <Checkbox
                                checked={medicalHistory[item.key] || false}
                                onChange={() => handleMedicalHistoryChange(item.key)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={item.label}
                    />
                ))}
            </div>
            <Row className="mt-2">
                <Col sm={12}>
                    <Label name="Other Medical Conditions" />
                    <TextField
                        type="text"
                        name="other_medical_conditions"
                        value={bookingData.other_medical_conditions}
                        onChange={handleChange}
                        placeholder="Specify any other medical conditions not listed above"
                    />
                </Col>
            </Row>

            <h6 className={classes.sectionTitle} style={{ marginTop: "20px" }}>
                Surgical History
            </h6>
            <p className="text-muted" style={{ fontSize: "12px", marginBottom: "8px" }}>
                Check all surgical procedures the mother has undergone.
            </p>
            <div className={classes.checkboxGrid}>
                {SURGICAL_HISTORY_CHECKLIST.map((item) => (
                    <FormControlLabel
                        key={item.key}
                        className={classes.checkboxLabel}
                        control={
                            <Checkbox
                                checked={surgicalHistory[item.key] || false}
                                onChange={() => handleSurgicalHistoryChange(item.key)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={item.label}
                    />
                ))}
            </div>
            <Row className="mt-2">
                <Col sm={12}>
                    <Label name="Other Surgical History" />
                    <TextField
                        type="text"
                        name="other_surgical_history"
                        value={bookingData.other_surgical_history}
                        onChange={handleChange}
                        placeholder="Specify any other surgeries not listed above"
                    />
                </Col>
            </Row>

            {/* Warning for previous C-section */}
            {surgicalHistory.cesarean_section && (
                <Alert severity="info" style={{ fontSize: "12px", marginTop: "12px" }}>
                    <strong>Previous Cesarean Section noted.</strong> This mother
                    requires close monitoring for VBAC (Vaginal Birth After Cesarean)
                    assessment or elective repeat C-section planning.
                </Alert>
            )}
        </div>
    );

    // ─── Step 2: Previous Pregnancies ────────────────────────────────
    const previousPregnanciesStep = (
        <div>
            <h6 className={classes.sectionTitle}>Previous Pregnancy History</h6>
            {gravida <= 1 ? (
                <Alert severity="info" style={{ fontSize: "12px" }}>
                    This is the mother's first pregnancy (Primigravida). No previous
                    pregnancy history to record.
                </Alert>
            ) : (
                <>
                    <p
                        className="text-muted"
                        style={{ fontSize: "12px", marginBottom: "10px" }}
                    >
                        Gravida {gravida} — please record details for{" "}
                        <strong>{gravida - 1}</strong> previous{" "}
                        {gravida - 1 === 1 ? "pregnancy" : "pregnancies"} (excluding
                        current).
                    </p>
                    <PreviousPregnancyTable
                        pregnancies={previousPregnancies}
                        onAdd={handleAddPregnancy}
                        onRemove={handleRemovePregnancy}
                        onChange={handlePregnancyChange}
                        deliveryModes={DELIVERY_MODE_OPTIONS}
                        deliveryPlaces={DELIVERY_PLACE_OPTIONS}
                        outcomes={OUTCOME_OPTIONS}
                        sexOptions={SEX_OPTIONS}
                        submitted={submitted}
                    />
                </>
            )}
        </div>
    );

    // ─── Step 3: Demographics & Next of Kin ──────────────────────────
    const demographicsStep = (
        <div>
            <h6 className={classes.sectionTitle}>Social & Demographic Information</h6>
            <Row>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Marital Status" />
                    <CustomSelect
                        name="marital_status"
                        value={MARITAL_STATUS_OPTIONS.find(
                            (o) => o.value === bookingData.marital_status
                        )}
                        onChange={(val) =>
                            handleSelectChange("marital_status", val)
                        }
                        options={MARITAL_STATUS_OPTIONS}
                        defaultValue={{ label: "Select...", value: "" }}
                    />
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Education Level" />
                    <CustomSelect
                        name="education_level"
                        value={EDUCATION_LEVEL_OPTIONS.find(
                            (o) => o.value === bookingData.education_level
                        )}
                        onChange={(val) =>
                            handleSelectChange("education_level", val)
                        }
                        options={EDUCATION_LEVEL_OPTIONS}
                        defaultValue={{ label: "Select...", value: "" }}
                    />
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Occupation" />
                    <TextField
                        type="text"
                        name="occupation"
                        value={bookingData.occupation}
                        onChange={handleChange}
                        placeholder="e.g. Teacher, Farmer"
                    />
                </Col>
            </Row>

            <h6 className={classes.sectionTitle} style={{ marginTop: "16px" }}>
                Next of Kin / Birth Companion
            </h6>
            <p className="text-muted" style={{ fontSize: "12px", marginBottom: "8px" }}>
                Person to contact in case of emergency and who will be involved in
                the birth plan.
            </p>
            <Row>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Name" />
                    <TextField
                        type="text"
                        name="next_of_kin_name"
                        value={bookingData.next_of_kin_name}
                        onChange={handleChange}
                        placeholder="Full name"
                    />
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Phone Number" />
                    <TextField
                        type="tel"
                        name="next_of_kin_phone"
                        value={bookingData.next_of_kin_phone}
                        onChange={handleChange}
                        placeholder="e.g. 0772-XXXXXX"
                    />
                </Col>
                <Col sm={6} md={4} className="mb-3">
                    <Label name="Relationship" />
                    <CustomSelect
                        name="next_of_kin_relationship"
                        value={RELATIONSHIP_OPTIONS.find(
                            (o) => o.value === bookingData.next_of_kin_relationship
                        )}
                        onChange={(val) =>
                            handleSelectChange("next_of_kin_relationship", val)
                        }
                        options={RELATIONSHIP_OPTIONS}
                        defaultValue={{ label: "Select...", value: "" }}
                    />
                </Col>
            </Row>
        </div>
    );

    // ─── Step Content Router ─────────────────────────────────────────
    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return clinicalInfoStep;
            case 1:
                return medicalHistoryStep;
            case 2:
                return previousPregnanciesStep;
            case 3:
                return demographicsStep;
            default:
                return null;
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            {/* Existing booking notice */}
            {isEditing && (
                <Alert severity="info" style={{ fontSize: "12px", marginBottom: "12px" }}>
                    Editing existing ANC booking. Changes will update the current
                    record.
                </Alert>
            )}

            {/* Gestational age summary bar */}
            {gestationalSummary}

            {/* Stepper */}
            <Stepper
                activeStep={activeStep}
                alternativeLabel
                className={classes.stepper}
            >
                {STEPS.map((label, index) => (
                    <Step
                        key={label}
                        completed={index < activeStep}
                        onClick={() => handleStepClick(index)}
                        style={{ cursor: "pointer" }}
                    >
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Step Content */}
            <Card className="p-3 mt-1">
                {getStepContent(activeStep)}
            </Card>

            {/* Navigation Buttons */}
            <RightAlignedContainer className="mt-3">
                <div style={{ display: "flex", gap: "8px", width: "100%", justifyContent: "space-between" }}>
                    <div>
                        {activeStep > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm sms-gray-btn"
                                onClick={handleBack}
                                disabled={isSubmitting}
                            >
                                Back
                            </button>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        {activeStep < STEPS.length - 1 ? (
                            <button
                                type="button"
                                className="btn btn-sm sms-info-btn"
                                onClick={handleNext}
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="btn btn-sm sms-btn"
                                disabled={isSubmitting || (!isEditing && !isFormValid())}
                            >
                                {isSubmitting
                                    ? "Saving..."
                                    : isEditing
                                    ? "Update ANC Booking"
                                    : "Save ANC Booking"}
                            </button>
                        )}
                    </div>
                </div>
            </RightAlignedContainer>
        </Form>
    );
};

export default ANCBookingForm;
